import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { MapPin, Plus, Edit2, Trash2, Building2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function LocationMaster({ user, onLogout }) {
  const [locations, setLocations] = useState([]);
  const [locationTypes, setLocationTypes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    state_code: '',
    gstin: '',
    location_type: '',
    phone: '',
    email: '',
    branch_manager_id: ''
  });

  useEffect(() => {
    fetchLocations();
    fetchLocationTypes();
  }, []);

  const fetchLocationTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/master-data/location-types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLocationTypes(response.data.location_types || []);
      // Set default location type if available
      if (response.data.location_types?.length > 0 && !formData.location_type) {
        setFormData(prev => ({ ...prev, location_type: response.data.location_types[0].name }));
      }
    } catch (error) {
      console.error('Failed to fetch location types:', error);
    }
  };

  const fetchLocations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/master-data/locations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLocations(response.data.locations || []);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      if (editingLocation) {
        await axios.put(`${API}/master-data/locations/${editingLocation.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Location updated successfully!');
      } else {
        await axios.post(`${API}/master-data/locations`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Location created successfully!');
      }

      resetForm();
      fetchLocations();
    } catch (error) {
      alert('Failed to save location: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEdit = (location) => {
    setEditingLocation(location);
    setFormData({
      name: location.name,
      address: location.address || '',
      city: location.city || '',
      state: location.state || '',
      state_code: location.state_code || '',
      gstin: location.gstin || '',
      location_type: location.location_type,
      phone: location.phone || '',
      email: location.email || '',
      branch_manager_id: location.branch_manager_id || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this location?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/master-data/locations/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Location deleted successfully!');
      fetchLocations();
    } catch (error) {
      alert('Failed to delete location: ' + (error.response?.data?.detail || error.message));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      address: '',
      city: '',
      state: '',
      state_code: '',
      gstin: '',
      location_type: 'Branch',
      phone: '',
      email: '',
      branch_manager_id: ''
    });
    setEditingLocation(null);
    setShowForm(false);
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>
              Location Master
            </h1>
            <p className="text-gray-600 mt-1">Manage office locations, branches, and warehouses with GST data</p>
          </div>
          <button
            onClick={() => {
              setShowForm(!showForm);
              if (showForm) resetForm();
            }}
            className="px-6 py-3 rounded-lg text-white font-medium flex items-center gap-2"
            style={{backgroundColor: '#2b3380'}}
          >
            <Plus className="w-5 h-5" />
            {showForm ? 'Cancel' : 'Add Location'}
          </button>
        </div>

        {/* Stats Card */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 mb-1">Total Locations</div>
            <div className="text-2xl font-bold" style={{color: '#2b3380'}}>{locations.length}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 mb-1">Head Offices</div>
            <div className="text-2xl font-bold text-purple-600">
              {locations.filter(l => l.location_type === 'Head Office').length}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 mb-1">Branches</div>
            <div className="text-2xl font-bold text-blue-600">
              {locations.filter(l => l.location_type === 'Branch').length}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 mb-1">Active Locations</div>
            <div className="text-2xl font-bold text-green-600">
              {locations.filter(l => l.is_active).length}
            </div>
          </div>
        </div>

        {/* Add/Edit Form */}
        {showForm && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-bold mb-4" style={{color: '#2b3380'}}>
              {editingLocation ? 'Edit Location' : 'Add New Location'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Location Name *</label>
                  <input
                    type="text"
                    placeholder="e.g., Mumbai Head Office"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Location Type *</label>
                  <select
                    value={formData.location_type}
                    onChange={(e) => setFormData({...formData, location_type: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  >
                    <option value="">Select Location Type</option>
                    {locationTypes.map(type => (
                      <option key={type.id} value={type.name}>
                        {type.icon ? `${type.icon} ${type.name}` : type.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Can&apos;t find your type? Go to Settings → Location Types to add custom types
                  </p>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Address</label>
                  <input
                    type="text"
                    placeholder="Complete address"
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">City *</label>
                  <input
                    type="text"
                    placeholder="e.g., Mumbai"
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">State *</label>
                  <input
                    type="text"
                    placeholder="e.g., Maharashtra"
                    value={formData.state}
                    onChange={(e) => setFormData({...formData, state: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">State Code (GST)</label>
                  <input
                    type="text"
                    placeholder="e.g., 27 for Maharashtra"
                    value={formData.state_code}
                    onChange={(e) => setFormData({...formData, state_code: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    maxLength="2"
                  />
                  <p className="text-xs text-gray-500 mt-1">2-digit GST state code</p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">GSTIN</label>
                  <input
                    type="text"
                    placeholder="e.g., 27AAAAA0000A1Z5"
                    value={formData.gstin}
                    onChange={(e) => setFormData({...formData, gstin: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    maxLength="15"
                  />
                  <p className="text-xs text-gray-500 mt-1">15-digit GST number</p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Phone</label>
                  <input
                    type="tel"
                    placeholder="+91 22 1234 5678"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Email</label>
                  <input
                    type="email"
                    placeholder="location@company.com"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="flex gap-2">
                <button
                  type="submit"
                  className="px-6 py-2 rounded-lg text-white"
                  style={{backgroundColor: '#2b3380'}}
                >
                  {editingLocation ? 'Update Location' : 'Create Location'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 py-2 rounded-lg border"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Locations Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <table className="w-full">
            <thead style={{backgroundColor: '#f5f7fa'}}>
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Location Details</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Type</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">City & State</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">GST Details</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Contact</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {locations.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                    <MapPin className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>No locations configured yet</p>
                    <p className="text-sm">Create your first location to get started</p>
                  </td>
                </tr>
              ) : (
                locations.map((location) => (
                  <tr key={location.id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-5 h-5" style={{color: '#2b3380'}} />
                        <div>
                          <div className="font-medium">{location.name}</div>
                          {location.address && (
                            <div className="text-sm text-gray-500">{location.address}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        location.location_type === 'Head Office' ? 'bg-purple-100 text-purple-700' :
                        location.location_type === 'Branch' ? 'bg-blue-100 text-blue-700' :
                        location.location_type === 'Warehouse' ? 'bg-orange-100 text-orange-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {location.location_type}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm">
                        <div>{location.city}</div>
                        <div className="text-gray-500">{location.state}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm">
                        {location.gstin ? (
                          <>
                            <div className="font-mono text-xs">{location.gstin}</div>
                            <div className="text-gray-500">Code: {location.state_code || 'N/A'}</div>
                          </>
                        ) : (
                          <span className="text-gray-400">No GST</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {location.phone && <div>{location.phone}</div>}
                      {location.email && <div className="text-gray-500">{location.email}</div>}
                      {!location.phone && !location.email && <span className="text-gray-400">-</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        location.is_active !== false
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {location.is_active !== false ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(location)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(location.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}

export default LocationMaster;
